// src/App.jsx
import React, { lazy, Suspense, useState, useEffect } from "react";
import Login from "./Login";
import Dashboard from "./Dashboard";
import ClientDashboard from "./ClientDashboard";
import Layout from "./Layout";
import { getCurrentUser, logout as logoutApi } from "./api";
import { getThemePreference, applyThemePreference } from "./theme";
import { getPortalRouteDocumentTitle } from "./documentTitle";
import BetaLayout from "./BetaLayout";
import BetaPublicShell from "./BetaPublicShell";
import {
    canonicalizePortalHash,
    getAuthenticatedLoginRedirectHash,
    getPortalExperience,
    getPortalRoutePage,
} from "./betaRouting";
import { isRemoteDeviceControlLabRoute } from "./recordingStudioBeta";

// Keep the shell and dashboard fast, but defer feature-heavy routes until they
// are actually visited. The recording list and invite form stay separate from
// the LiveKit/Yjs studio runtime until a user opens or joins a studio.
const loadSettingsModule = () => import("./Settings");
const loadClientsModule = () => import("./Clients");
const loadPodcastsModule = () => import("./Podcasts");
const loadAnalyticsModule = () => import("./Analytics");
const loadEpisodesModule = () => import("./Episodes");
const loadFileExplorerModule = () => import("./FileExplorer");
const loadLeadsModule = () => import("./Leads");
const loadMeetingRequestsModule = () => import("./MeetingRequests");
const loadUserManagementModule = () => import("./UserManagement");
const loadBillingModule = () => import("./Billing");

const Settings = lazy(loadSettingsModule);
const Clients = lazy(loadClientsModule);
const Documents = lazy(() => import('./Documents'));
const Podcasts = lazy(loadPodcastsModule);
const Analytics = lazy(loadAnalyticsModule);
const Episodes = lazy(loadEpisodesModule);
const FileExplorer = lazy(loadFileExplorerModule);
const Leads = lazy(loadLeadsModule);
const ReviewPage = lazy(() => import("./ReviewPage"));
const DocumentSigningPage = lazy(() => import('./DocumentSigningPage'));
const LeadOnboardingPage = lazy(() => import("./LeadOnboardingPage"));
const DiscoveryCallPage = lazy(() => import("./DiscoveryCallPage"));
const BookDiscoveryPage = lazy(() => import("./BookDiscoveryPage"));
const MeetingRequests = lazy(loadMeetingRequestsModule);
const MeetingRequestPage = lazy(() => import("./MeetingRequestPage"));
const EpisodeSharePage = lazy(() => import("./EpisodeSharePage"));
const SetPasswordPage = lazy(() => import("./SetPasswordPage"));
const UserManagement = lazy(loadUserManagementModule);
const Billing = lazy(loadBillingModule);
const RecordingSessionJoin = lazy(() => import("./RecordingSessionJoin"));

let recordingSessionsModulePromise;
function loadRecordingSessionsModule() {
    recordingSessionsModulePromise ||= import("./RecordingSessions");
    return recordingSessionsModulePromise;
}
let recordingStudioModulePromise;
function loadRecordingStudioModule() {
    recordingStudioModulePromise ||= import("./RecordingStudio");
    return recordingStudioModulePromise;
}

const RecordingSessions = lazy(loadRecordingSessionsModule);
const RecordingStudio = lazy(() =>
    loadRecordingStudioModule().then(({ RecordingStudio: Component }) => ({
        default: Component,
    }))
);
const SoloRecorder = lazy(() =>
    loadRecordingStudioModule().then(({ SoloRecorder: Component }) => ({
        default: Component,
    }))
);

function preloadAuthenticatedRoute(routePage) {
    if (!routePage) return null;
    if (routePage.startsWith("recording-studio")) return loadRecordingStudioModule();
    if (routePage.startsWith("recording-sessions/join")) return null;
    if (routePage.startsWith("recording-sessions")) return loadRecordingSessionsModule();
    if (routePage.startsWith("episodes")) return loadEpisodesModule();
    if (routePage === "settings") return loadSettingsModule();
    if (routePage === "clients") return loadClientsModule();
    if (routePage === "podcasts") return loadPodcastsModule();
    if (routePage === "analytics") return loadAnalyticsModule();
    if (routePage === "files") return loadFileExplorerModule();
    if (routePage === "leads") return loadLeadsModule();
    if (routePage === "meetings") return loadMeetingRequestsModule();
    if (routePage === "user-management") return loadUserManagementModule();
    if (routePage === "billing") return loadBillingModule();
    return null;
}

const DeferredRoute = ({ children }) => (
    <Suspense fallback={<div style={{ padding: 24 }}>Loading…</div>}>
        {children}
    </Suspense>
);

function App() {
    const [token, setToken] = useState(localStorage.getItem("token"));
    const [user, setUser] = useState(null);
    const [page, setPage] = useState(() => (
        (window.location.hash || "#/dashboard").replace(/^#\/?/, "") || "dashboard"
    ));

    // Initialise theme (system / light / dark) for the whole app
    useEffect(() => {
        try {
            const pref = getThemePreference ? getThemePreference() : "system";
            if (applyThemePreference) {
                applyThemePreference(pref);
            }
        } catch (err) {
            // If anything goes wrong, just fail silently – UI will still work
            console.error("Failed to apply theme preference:", err);
        }
    }, []);

    const handleLoginSuccess = (userData) => {
        setUser(userData);
        setToken(localStorage.getItem("token"));
    };

    const handleCurrentUserChanged = (userData, nextToken) => {
        if (nextToken) {
            localStorage.setItem("token", nextToken);
            setToken(nextToken);
        }
        setUser(userData);
    };

    const handleLogout = async ({ notifyServer = true } = {}) => {
        if (notifyServer && token) {
            try {
                await logoutApi();
            } catch (err) {
                // best-effort audit event; continue local logout
                console.error("Logout API call failed:", err);
            }
        }
        localStorage.removeItem("token");
        setUser(null);
        setToken(null);
    };

    useEffect(() => {
        let cancelled = false;

        async function hydrateUser() {
            if (!token) return;
            if (user) return;

            try {
                const res = await getCurrentUser();
                if (cancelled) return;

                if (!res || !res.ok || !res.user) {
                    handleLogout({ notifyServer: false });
                    return;
                }

                setUser(res.user);
            } catch (err) {
                console.error("Failed to hydrate current user:", err);
                if (!cancelled) {
                    handleLogout({ notifyServer: false });
                }
            }
        }

        hydrateUser();
        return () => {
            cancelled = true;
        };
    }, [token, user]);

    useEffect(() => {
        function onHashChange() {
            const currentHash = window.location.hash || "#/dashboard";
            const nextHash = canonicalizePortalHash(currentHash);
            if (nextHash !== currentHash) {
                window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
            }
            setPage(nextHash.replace(/^#\/?/, "") || "dashboard");
        }

        window.addEventListener("hashchange", onHashChange);
        onHashChange();

        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);

    const portalExperience = getPortalExperience(page);
    const isModernPortal = portalExperience === "modern";
    const routePage = getPortalRoutePage(page);
    const remoteDeviceControlLabEnabled = isRemoteDeviceControlLabRoute(page);
    const renderPublicPage = (content, options = {}) => (
        isModernPortal ? <BetaPublicShell {...options}>{content}</BetaPublicShell> : content
    );

    useEffect(() => {
        if (!token) return;
        const preloadPromise = preloadAuthenticatedRoute(routePage);
        preloadPromise?.catch(() => {
            // The route-level Suspense boundary will surface a later retry failure.
        });
    }, [routePage, token]);

    useEffect(() => {
        document.title = getPortalRouteDocumentTitle(page, {
            authenticated: Boolean(token),
        });
    }, [page, token]);

    const authenticatedLoginRedirect = getAuthenticatedLoginRedirectHash(
        window.location.hash,
        Boolean(token && user)
    );

    useEffect(() => {
        if (!authenticatedLoginRedirect) return;
        window.history.replaceState(
            null,
            "",
            `${window.location.pathname}${window.location.search}${authenticatedLoginRedirect}`
        );
        setPage(authenticatedLoginRedirect.replace(/^#\/?/, "") || "dashboard");
    }, [authenticatedLoginRedirect]);

    // 🔓 PUBLIC MAGIC-LINK REVIEW ROUTE (NO LOGIN)
    if (routePage === 'document-sign' || routePage === 'document-download') {
        return renderPublicPage(<DeferredRoute><DocumentSigningPage key={page} mode={routePage === 'document-download' ? 'download' : 'client'} /></DeferredRoute>);
    }
    // Link format: https://your-portal-url/#/review?token=ABC123
    if (routePage && routePage.startsWith("review")) {
        return renderPublicPage(<DeferredRoute><ReviewPage /></DeferredRoute>);
    }

    if (routePage && routePage.startsWith("onboarding")) {
        return renderPublicPage(<DeferredRoute><LeadOnboardingPage /></DeferredRoute>);
    }

    if (routePage && routePage.startsWith("discovery-call")) {
        return renderPublicPage(<DeferredRoute><DiscoveryCallPage /></DeferredRoute>);
    }

    if (routePage && routePage.startsWith("book-discovery")) {
        return renderPublicPage(<DeferredRoute><BookDiscoveryPage /></DeferredRoute>);
    }

    if (routePage && routePage.startsWith("meeting-request")) {
        return renderPublicPage(<DeferredRoute><MeetingRequestPage /></DeferredRoute>);
    }

    if (routePage && routePage.startsWith("episode-share")) {
        return renderPublicPage(<DeferredRoute><EpisodeSharePage /></DeferredRoute>);
    }

    if (routePage && routePage.startsWith("recording-sessions/join")) {
        return renderPublicPage(
            <DeferredRoute>
                <RecordingSessionJoin
                    experience={isModernPortal ? "beta" : "live"}
                    uploadStrategy={isModernPortal ? "adaptive-upload-v1" : undefined}
                    remoteDeviceControlEnabled={remoteDeviceControlLabEnabled}
                />
            </DeferredRoute>,
            isModernPortal ? { immersive: true } : {}
        );
    }

    if (routePage && routePage.startsWith("set-password")) {
        return renderPublicPage(
            <DeferredRoute>
                <SetPasswordPage onLoginSuccess={handleLoginSuccess} />
            </DeferredRoute>
        );
    }

    // AUTH-ONLY PORTAL BELOW HERE
    if (!token) {
        return renderPublicPage(<Login onLoginSuccess={handleLoginSuccess} />);
    }

    if (!user) {
        return renderPublicPage(
            <div className="beta-public-loading" style={{ padding: 24 }}>Loading user…</div>
        );
    }

    if (authenticatedLoginRedirect) {
        return renderPublicPage(
            <div className="beta-public-loading" style={{ padding: 24 }}>Opening dashboard…</div>
        );
    }

    if (routePage && routePage.startsWith("recording-studio")) {
        const parts = routePage.split("/").filter(Boolean);
        const sessionId = parts[1] || "";
        const mode = parts[2] || "studio";

        return renderPublicPage(
            <DeferredRoute>
                {mode === "solo"
                    ? <SoloRecorder sessionId={sessionId} experience={isModernPortal ? "beta" : "live"} />
                    : (
                        <RecordingStudio
                            sessionId={sessionId}
                            experience={isModernPortal ? "beta" : "live"}
                            uploadStrategy={isModernPortal ? "adaptive-upload-v1" : undefined}
                            remoteDeviceControlEnabled={remoteDeviceControlLabEnabled}
                        />
                    )}
            </DeferredRoute>,
            isModernPortal ? { forceDark: true, immersive: true } : {}
        );
    }

    const isClient = user.role === "client";
    if (routePage.startsWith('document-countersign/')) {
        return renderPublicPage(user.role === 'admin'
            ? <DeferredRoute><DocumentSigningPage key={routePage} mode="admin" documentId={routePage.split('/')[1]} currentUser={user} /></DeferredRoute>
            : <p>Admin privileges are required to countersign documents.</p>);
    }
    const canViewBilling = user.role === "admin" || Boolean(user.has_billing_access);
    const canViewAnalytics = user.role === "admin" || Boolean(user.has_analytics_access);
    let content = null;

    if (isClient) {
        if (routePage === "dashboard") {
            content = (
                <ClientDashboard
                    onLogout={handleLogout}
                    embedded
                    beta={isModernPortal}
                />
            );
        } else if (routePage === "settings") {
            content = (
                <Settings
                    token={token}
                    onLogout={handleLogout}
                    onCurrentUserChanged={handleCurrentUserChanged}
                    embedded
                    beta={isModernPortal}
                />
            );
        } else if (routePage === "analytics" && canViewAnalytics) {
            content = <Analytics onLogout={handleLogout} embedded currentUser={user} />;
        } else if (isModernPortal && routePage === "billing" && canViewBilling) {
            content = <Billing onLogout={handleLogout} embedded user={user} />;
        } else if (routePage === "files") {
            content = <FileExplorer onLogout={handleLogout} embedded currentUser={user} />;
        } else if (routePage.startsWith("episodes")) {
            content = (
                <Episodes
                    token={token}
                    onLogout={handleLogout}
                    embedded
                    currentUser={user}
                />
            );
        } else if (
            routePage.startsWith("recording-sessions") &&
            user.has_recording_sessions_access
        ) {
            content = <RecordingSessions onLogout={handleLogout} embedded />;
        } else {
            content = <ClientDashboard onLogout={handleLogout} embedded />;
        }
        const Shell = isModernPortal ? BetaLayout : Layout;
        return (
            <Shell onLogout={handleLogout} currentUser={user}>
                <DeferredRoute>{content}</DeferredRoute>
            </Shell>
        );
    }

    // ROUTING
    if (routePage === "settings") {
        content = (
            <Settings
                token={token}
                onLogout={handleLogout}
                onCurrentUserChanged={handleCurrentUserChanged}
                embedded
                beta={isModernPortal}
        />
    );
    } else if (routePage === 'documents' || routePage === 'documents/settings') {
        content = <Documents currentUser={user} settings={routePage === 'documents/settings'} onLogout={handleLogout} embedded />;
    } else if (routePage === "analytics" && canViewAnalytics) {
        content = <Analytics onLogout={handleLogout} embedded currentUser={user} />;
    } else if (isModernPortal && routePage === "billing" && canViewBilling) {
        content = <Billing onLogout={handleLogout} embedded user={user} />;
    } else if (routePage === "clients") {
        content = <Clients token={token} onLogout={handleLogout} embedded />;
    } else if (routePage === "leads") {
        content = <Leads token={token} onLogout={handleLogout} embedded />;
    } else if (routePage === "meetings") {
        content = <MeetingRequests onLogout={handleLogout} embedded />;
    } else if (routePage === "podcasts") {
        content = <Podcasts token={token} onLogout={handleLogout} embedded />;
    } else if (routePage === "files") {
        content = <FileExplorer onLogout={handleLogout} embedded currentUser={user} />;
    } else if (routePage === "user-management") {
        content = (
            <UserManagement token={token} onLogout={handleLogout} embedded />
        );
    } else if (
        routePage.startsWith("recording-sessions") &&
        user.has_recording_sessions_access
    ) {
        content = <RecordingSessions onLogout={handleLogout} embedded />;
    } else if (routePage.startsWith("episodes")) {
        content = (
            <Episodes
                token={token}
                onLogout={handleLogout}
                embedded
                currentUser={user}
            />
        );
    } else if (routePage === "dashboard" || isModernPortal && routePage === "") {
        content = <Dashboard token={token} onLogout={handleLogout} embedded beta={isModernPortal} />;
    } else {
        // Fallback
        content = <Dashboard token={token} onLogout={handleLogout} embedded />;
    }
    const Shell = isModernPortal ? BetaLayout : Layout;
    return (
        <Shell onLogout={handleLogout} currentUser={user}>
            <DeferredRoute>{content}</DeferredRoute>
        </Shell>
    );
}

export default App;
